/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, type CSSProperties } from "react";
import { Info, OctagonAlert } from "lucide-react";
import { Button } from "../components/ui/Button";
import { ToggleButton } from "../components/ui/ToggleButton";
import { useChrome } from "../contexts/ChromeContext";
import { cn } from "../lib/utils";

const ON_OFF = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

const RUN_PAUSE = [
  { value: "running", label: "Running" },
  { value: "paused", label: "Paused" },
];

type Guardrail = {
  id: string;
  title: string;
  description: string;
  /** The threshold or value this guardrail enforces; omitted when it is simply on/off. */
  value?: string;
  defaultOn: boolean;
};

const GUARDRAILS: Guardrail[] = [
  {
    id: "auto-book-gate",
    title: "Auto-book confidence gate",
    description:
      "Agents book a slot only above this match confidence. Below it, the case routes to a coordinator.",
    value: "0.80",
    defaultOn: true,
  },
  {
    id: "escalation-floor",
    title: "Escalation floor",
    description: "Any decision under this confidence pages a human, not just queues it.",
    value: "0.30",
    defaultOn: true,
  },
  {
    id: "rate-limit",
    title: "Outreach rate limit",
    description: "At most one outreach to a family's phone within this window.",
    value: "24h",
    defaultOn: true,
  },
  {
    id: "one-offer",
    title: "One offer per phone",
    description: "Never two live offers to the same shared family phone at once.",
    value: "Enforced",
    defaultOn: true,
  },
  {
    id: "timing",
    title: "Reminder & timeout windows",
    description: "Nudge the family at the first mark, then move the slot on at the second.",
    value: "45m / 60m",
    defaultOn: true,
  },
  {
    id: "do-not-contact",
    title: "Do-not-contact & STOP",
    description: "Honor STOP replies and do-not-contact tags before any send.",
    value: "Always",
    defaultOn: true,
  },
];

export function Guardrails() {
  useChrome({ title: "Guardrails" }, []);
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(GUARDRAILS.map((g) => [g.id, g.defaultOn])),
  );
  const [paused, setPaused] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-4">
      <header className="enter max-w-2xl">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Guardrails
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          The rules that decide when an agent may act alone and when it must hand to a human. This is
          the contract behind every confidence score.
        </p>
      </header>

      <div className="space-y-3">
        {GUARDRAILS.map((g, index) => (
          <div
            key={g.id}
            className="enter border border-border bg-bg-elevated shadow-card flex items-center gap-4 rounded-2xl p-4"
            style={{ "--enter-delay": `${Math.min(index, 8) * 40 + 60}ms` } as CSSProperties}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-medium text-text-primary">{g.title}</p>
              <p className="mt-0.5 text-[13px] leading-relaxed text-text-secondary">
                {g.description}
              </p>
            </div>
            {g.value !== undefined && (
              <span className="shrink-0 rounded-lg border border-accent/30 bg-accent/10 px-2.5 py-1 font-mono text-xs font-medium text-accent">
                {g.value}
              </span>
            )}
            <ToggleButton
              className="w-28 shrink-0"
              options={ON_OFF}
              value={(enabled[g.id] ?? false) ? "on" : "off"}
              onChange={(v) => setEnabled((prev) => ({ ...prev, [g.id]: v === "on" }))}
            />
          </div>
        ))}
      </div>

      <div
        className={cn(
          "flex items-center gap-4 rounded-2xl border p-4 transition-colors",
          paused
            ? "border-warning-border bg-warning-bg"
            : "border-border bg-bg-elevated/50",
        )}
      >
        <OctagonAlert size={20} className={cn("shrink-0", paused ? "text-warning" : "text-text-tertiary")} />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-medium text-text-primary">Emergency stop</p>
          <p className="mt-0.5 text-[13px] leading-relaxed text-text-secondary">
            {paused
              ? "All agent sends are paused. Resume to let the scheduling agent act again."
              : "Pause every agent send immediately — the kill switch for all outreach."}
          </p>
        </div>
        <ToggleButton
          className="w-40 shrink-0"
          options={RUN_PAUSE}
          value={paused ? "paused" : "running"}
          onChange={(v) => setPaused(v === "paused")}
        />
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-border/60 pt-4">
        <p className="flex items-center gap-1.5 text-xs text-text-tertiary">
          <Info size={13} />
          Changes apply on the next agent restart.
        </p>
        <Button size="sm">Save changes</Button>
      </div>
    </div>
  );
}

